<template>
  <div class="theme-gauge-chart" ref="container">
    <canvas ref="canvas" class="theme-gauge-chart__canvas"></canvas>
  </div>
</template>

<script>
export default {
  name: 'ThemeGaugeChart',
  
  props: {
    value: {
      type: Number,
      required: true,
      default: 0
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    greenZone: {
      type: Array,
      default: () => [0, 30]
    },
    yellowZone: {
      type: Array,
      default: () => [30, 60]
    },
    redZone: {
      type: Array,
      default: () => [60, 100]
    },
    showLabels: {
      type: Boolean,
      default: true
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      default: 250
    }
  },

  data() {
    return {
      isDarkMode: document.documentElement.classList.contains('dark'),
      themeObserver: null,
      animatedValue: this.min,
      animationFrameId: null,
      resizeObserver: null,
      ctx: null
    };
  },

  mounted() {
    this.initCanvas();
    this.setupThemeObserver();
    this.setupResizeObserver();
    
    this.$nextTick(() => {
      this.animateNeedle(this.value);
    });
  },

  watch: {
    value(newValue) {
      this.animateNeedle(newValue);
    },
    isDarkMode() {
      this.draw();
    }
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },

  methods: {
    initCanvas() {
      const canvas = this.$refs.canvas;
      const container = this.$refs.container;
      
      // Set canvas size based on container or props
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      
      canvas.width = (rect.width || this.width) * dpr;
      canvas.height = (rect.height || this.height) * dpr;
      canvas.style.width = `${rect.width || this.width}px`;
      canvas.style.height = `${rect.height || this.height}px`;
      
      this.ctx = canvas.getContext('2d');
      this.ctx.scale(dpr, dpr);
      
      this.draw();
    },

    setupThemeObserver() {
      this.themeObserver = new MutationObserver(() => {
        this.isDarkMode = document.documentElement.classList.contains('dark');
      });
      
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    },

    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.initCanvas();
      });
      
      this.resizeObserver.observe(this.$refs.container);
    },

    animateNeedle(targetValue) {
      const startValue = this.animatedValue;
      const startTime = performance.now();
      const duration = 1000;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out cubic)
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        this.animatedValue = startValue + (targetValue - startValue) * easeProgress;
        this.draw();
        
        if (progress < 1) {
          this.animationFrameId = requestAnimationFrame(animate);
        } else {
          this.animatedValue = targetValue;
          this.draw();
        }
      };
      
      this.animationFrameId = requestAnimationFrame(animate);
    },

    draw() {
      if (!this.ctx) return;
      
      const canvas = this.$refs.canvas;
      const width = canvas.style.width ? parseFloat(canvas.style.width) : this.width;
      const height = canvas.style.height ? parseFloat(canvas.style.height) : this.height;
      
      // Clear canvas
      this.ctx.clearRect(0, 0, width, height);
      
      // Calculate center and dimensions
      const cx = width / 2;
      const cy = height * 0.75;
      const radius = Math.min(width, height) * 0.35;
      const arcWidth = radius * 0.25;
      
      // Draw gauge arcs
      this.drawGaugeArcs(cx, cy, radius, arcWidth);
      
      // Draw labels if enabled
      if (this.showLabels) {
        this.drawLabels(cx, cy, radius + arcWidth + 30);
      }
      
      // Draw needle
      this.drawNeedle(cx, cy, radius * 0.85);
      
      // Draw center circle
      this.drawCenterCircle(cx, cy);
    },

    drawGaugeArcs(cx, cy, radius, arcWidth) {
      const range = this.max - this.min;
      
      // Define zones with colors
      const zones = [
        { range: this.greenZone, color: '#22c55e' },
        { range: this.yellowZone, color: '#eab308' },
        { range: this.redZone, color: '#ef4444' }
      ];
      
      zones.forEach(zone => {
        const startPercent = (zone.range[0] - this.min) / range;
        const endPercent = (zone.range[1] - this.min) / range;
        const startAngle = Math.PI + (startPercent * Math.PI);
        const endAngle = Math.PI + (endPercent * Math.PI);
        
        this.ctx.beginPath();
        this.ctx.arc(cx, cy, radius, startAngle, endAngle);
        this.ctx.lineWidth = arcWidth;
        this.ctx.strokeStyle = zone.color;
        this.ctx.lineCap = 'butt'; // Flat ends instead of rounded
        this.ctx.stroke();
      });
    },

    drawLabels(cx, cy, labelRadius) {
      const range = this.max - this.min;
      const textColor = this.isDarkMode ? '#94a3b8' : '#6b7280';
      
      // Generate 5 evenly spaced markers based on min/max
      const markers = [
        this.min,
        this.min + range * 0.25,
        this.min + range * 0.5,
        this.min + range * 0.75,
        this.max
      ];
      
      this.ctx.save();
      this.ctx.font = '14px sans-serif';
      this.ctx.fillStyle = textColor;
      
      markers.forEach((marker, index) => {
        const markerPercent = (marker - this.min) / range;
        const markerAngle = Math.PI + (markerPercent * Math.PI);
        
        // Use smaller radius for center label (top) to reduce gap
        const adjustedRadius = index === 2 ? labelRadius - 15 : labelRadius;
        
        let markerX = cx + Math.cos(markerAngle) * adjustedRadius;
        let markerY = cy + Math.sin(markerAngle) * adjustedRadius;
        
        // Special positioning adjustments based on position
        if (index === 2) {
          // Center marker (50%) - already closer due to adjustedRadius
          markerY -= 5;
        } else if (index === 0) {
          // Left marker (min) - adjust slightly
          markerX -= 5;
          markerY += 2;
        } else if (index === 4) {
          // Right marker (max) - adjust slightly
          markerX += 5;
          markerY += 2;
        } else if (index === 1) {
          // 25% marker
          markerX -= 2;
          markerY += 3;
        } else if (index === 3) {
          // 75% marker
          markerX += 2;
          markerY += 3;
        }
        
        // Text alignment based on position
        if (index === 0) {
          this.ctx.textAlign = 'left';
        } else if (index === 4) {
          this.ctx.textAlign = 'right';
        } else {
          this.ctx.textAlign = 'center';
        }
        this.ctx.textBaseline = 'middle';
        
        // Format the label - round to whole number and add %
        const labelValue = Math.round(marker);
        this.ctx.fillText(labelValue + '%', markerX, markerY);
      });
      
      this.ctx.restore();
    },

    drawNeedle(cx, cy, needleLength) {
      const range = this.max - this.min;
      // Clamp to the arc. An out-of-range value swings the needle past the end of
      // the gauge and into dead space below the baseline — a portfolio benchmark
      // of +128% on a [-100, 100] gauge pointed down and to the right, reading as
      // though it were negative.
      const valuePercent = Math.min(1, Math.max(0, (this.animatedValue - this.min) / range));
      // Start from left (Math.PI) and rotate clockwise
      const angle = Math.PI + (valuePercent * Math.PI);
      
      this.ctx.save();
      this.ctx.translate(cx, cy);
      this.ctx.rotate(angle);
      
      // Shadow
      this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      this.ctx.shadowBlur = 4;
      this.ctx.shadowOffsetX = 2;
      this.ctx.shadowOffsetY = 2;
      
      // Needle - draw from center outward (positive Y in rotated space)
      this.ctx.beginPath();
      this.ctx.strokeStyle = this.isDarkMode ? '#fbbf24' : '#f59e0b';
      this.ctx.lineWidth = 8;
      this.ctx.lineCap = 'round';
      this.ctx.moveTo(0, 0);
      this.ctx.lineTo(needleLength, 0); // Draw horizontally in rotated space
      this.ctx.stroke();
      
      this.ctx.restore();
    },

    drawCenterCircle(cx, cy) {
      this.ctx.save();
      this.ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      this.ctx.shadowBlur = 3;
      
      // Outer circle
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, 14, 0, 2 * Math.PI);
      this.ctx.fillStyle = this.isDarkMode ? '#0f172a' : '#1e293b';
      this.ctx.fill();
      
      // Middle circle
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, 12, 0, 2 * Math.PI);
      this.ctx.fillStyle = this.isDarkMode ? '#1e293b' : '#000000';
      this.ctx.fill();
      
      // Inner circle
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, 8, 0, 2 * Math.PI);
      this.ctx.fillStyle = this.isDarkMode ? '#fbbf24' : '#f59e0b';
      this.ctx.fill();
      
      this.ctx.restore();
    }
  }
};
</script>

<style scoped>
.theme-gauge-chart {
  width: 100%;
  height: 100%;
  position: relative;
  min-height: 200px;
}

.theme-gauge-chart__canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
